import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { api, type CustomerContextResponse } from '../../../lib/api-client';
import { WS_EVENTS, type PersonContextRefreshedPayload } from '../../../lib/websocket/events';
import { useWebSocket } from '../../../lib/websocket/WebSocketProvider';

/**
 * useCustomerContext — substitui fixture de Item NEW-C6 por integração real.
 *
 * Consome GET /v1/conversations/:id/customer-context (PR 3 sub-fatia 10.0).
 * Listener WS `person.context.refreshed` invalida React Query quando o
 * Engine completa um refresh (auto-trigger pelo listener `message.received`
 * do PR 2 OU manual pelo verbo "Atualizar do conector").
 *
 * Quando o payload do backend NÃO inclui `externalSources` (tenant sem
 * capability `external-context-sync`), o ContextBlock renderiza apenas
 * linhas internal + rag — regra do silêncio.
 */
export function useCustomerContext(conversationId: string): {
  data: CustomerContextResponse | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const queryClient = useQueryClient();
  const { subscribe } = useWebSocket();

  const query = useQuery<CustomerContextResponse>({
    queryKey: ['customer-context', conversationId],
    queryFn: async () => api.getCustomerContext(conversationId),
    staleTime: 30_000,
  });

  useEffect(() => {
    const unsubscribe = subscribe<PersonContextRefreshedPayload>(
      WS_EVENTS.PERSON_CONTEXT_REFRESHED,
      () => {
        void queryClient.invalidateQueries({ queryKey: ['customer-context', conversationId] });
      },
    );
    return () => unsubscribe();
  }, [conversationId, queryClient, subscribe]);

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
